import { ThumbDownAlt, ThumbUpAlt } from "@mui/icons-material";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useEventLog, useLocalStorage } from "../../hooks";
import { EActionType } from "../../models/event-logs";
import { useVideoController } from "../../stores";

type TOwnProps = {
  videoId: string | null;
  src: string;
  name: string;
  originalName: string;
  rootPath: string;
};

type TVideoState = {
  isLike?: boolean;
  isDislike?: boolean;
};

const VideoPlayer = (props: TOwnProps) => {
  const { src, name } = props;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [searchParams, _] = useSearchParams();

  const { logAsset, logFeedback, isLoggingFeedback, gerenateEventLogPayload } =
    useEventLog();
  const { timeStamp, setVideoTimeStamp, deleteTimeStamp } = useLocalStorage();

  const channelPath = searchParams.get("channelPath") || "";
  const [videoState, setVideoState] = useState<TVideoState>();

  const { muted, intensity, setVolume, setMuted, setCurrentVideo } =
    useVideoController();

  useEffect(() => {
    if (videoRef.current !== null) videoRef.current.volume = intensity / 100;
  }, [intensity]);

  useEffect(() => {
    if (videoRef.current !== null) videoRef.current.muted = muted;
  }, [muted]);

  const handleVolumeChange = (e: ChangeEvent<HTMLVideoElement>) => {
    setMuted(e.target.muted ?? muted);
    setVolume((e.target.volume ?? 0) * 100);
  };

  const onLike = () => {
    if (!videoState?.isLike) {
      const payload = gerenateEventLogPayload({
        actionType: EActionType.LIKE,
        channelPath,
        videoTitle: name,
        src,
      });
      logFeedback(payload);
    }
    setVideoState((prev) => ({
      isDislike: false,
      isLike: !prev?.isLike,
    }));
  };

  const onDislike = () => {
    if (!videoState?.isDislike) {
      const payload = gerenateEventLogPayload({
        actionType: EActionType.DISLIKE,
        channelPath,
        videoTitle: name,
        src,
      });
      logFeedback(payload);
    }
    setVideoState((prev) => ({
      isLike: false,
      isDislike: !prev?.isDislike,
    }));
  };

  useEffect(() => {
    setCurrentVideo(videoRef.current);
    //eslint-disable-next-line
  }, [videoRef]);

  // const captureVideoProgress = () => {
  //   if (videoRef.current !== null && videoId !== null) {
  //     const currentVideoTime = videoRef.current.currentTime;

  //     // READ video object from Local Storage
  //     const viewedVideos = localStorage.getItem("videos") || "{}";

  //     // Merge with current Videos
  //     const parseVideoObj = JSON.stringify({
  //       ...JSON.parse(viewedVideos),
  //       [videoId]: currentVideoTime,
  //     });

  //     localStorage.setItem("videos", parseVideoObj);
  //     return videoRef.current.currentTime;
  //   }
  // };

  // DO NOT DELET - Used for Fake Video Player data
  // const tempSrc = `http://localhost:5050/static-files/A%20Womans%20Calling/assets/videos/A_Womans_Calling.webm`;

  // // WATCHER: Init video
  // useEffect(() => {
  //   const viewedVideo = videoId && localStorage.getItem("videos");
  //   if (videoRef.current !== null && viewedVideo) {
  //     const viewedVideos = JSON.parse(viewedVideo);
  //     videoRef.current.currentTime = viewedVideos[videoId] || 0;
  //   }
  //   // Trigger first loaded ONLY - No need to pass any dependency params
  //   //eslint-disable-next-line
  // }, []);

  // Capture video progress
  // captureVideoProgress();

  // WATCHER: Observe timestamp to trigger Event when user leaving the page
  useEffect(() => {
    return () => {
      // Not trigger when Paused
      if (videoRef.current?.paused) {
        return;
      }
      const videoProgressInSec = Number(timeStamp);

      // Trigger log event when video loaded
      if (Boolean(videoProgressInSec) && videoProgressInSec > 1) {
        const payload = gerenateEventLogPayload({
          actionType: EActionType.STOP,
          channelPath,
          videoTitle: name,
          src,
        });

        logAsset({
          ...payload,
          secsDuration: videoProgressInSec,
        });
      }

      deleteTimeStamp();
    };
  }, [timeStamp, videoRef]);

  return (
    <Grid
      sx={{
        "& video": {
          width: "100%",
          height: "calc(100vh - 300px)",
        },
      }}
    >
      <video
        controls
        autoPlay
        ref={videoRef}
        muted={muted}
        controlsList="nodownload"
        onVolumeChange={handleVolumeChange}
        onEnded={(e) => {
          const payload = gerenateEventLogPayload({
            actionType: EActionType.COMPLETED,
            channelPath,
            videoTitle: name,
            src,
          });
          logAsset({
            ...payload,
            videoLength: Math.floor(e.currentTarget.currentTime),
          });
        }}
        onPause={(e) => {
          // SHOULD trigger the event log when video paused as completed
          if (videoRef.current?.duration === videoRef.current?.currentTime) {
            return;
          }

          const payload = gerenateEventLogPayload({
            actionType: EActionType.PAUSE,
            channelPath,
            videoTitle: name,
            src,
          });
          logAsset({
            ...payload,
            secsDuration: Number(videoRef.current?.currentTime.toFixed(0)),
          });
        }}
        onPlay={() => {
          const payload = gerenateEventLogPayload({
            actionType: EActionType.PLAY,
            channelPath,
            videoTitle: name,
            src,
          });
          logAsset({
            ...payload,
            videoLength: Number(videoRef.current?.currentTime.toFixed(0)),
          });
        }}
        onTimeUpdateCapture={(e) => {
          setVideoTimeStamp(e.currentTarget.currentTime);
        }}
      >
        <source src={src} />
      </video>

      <Stack direction="row" alignItems="center">
        <Typography variant="h5">{name}</Typography>

        <Box
          display="flex"
          flexDirection="row"
          sx={{
            marginLeft: "2rem",
            border: "1px solid white",
            borderRadius: "16px",
            padding: "0.1rem 0.5rem",
          }}
        >
          <IconButton onClick={onLike} disabled={isLoggingFeedback}>
            <ThumbUpAlt
              sx={(theme) => ({
                color: videoState?.isLike
                  ? theme.palette.primary.main
                  : "white",
              })}
            />
          </IconButton>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "white", margin: "5px" }}
          />
          <IconButton onClick={onDislike} disabled={isLoggingFeedback}>
            <ThumbDownAlt
              sx={(theme) => ({
                color: videoState?.isDislike
                  ? theme.palette.primary.main
                  : "white",
              })}
            />
          </IconButton>
        </Box>
      </Stack>
    </Grid>
  );
};

export default VideoPlayer;
